/**
 * Tooltip Component
 * Tooltip components based on Radix UI Tooltip primitive.
 * @packageDocumentation
 */

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, type ElementRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/utils';

/** Tooltip provider component. */
const TooltipProvider = TooltipPrimitive.Provider;

/** Tooltip root component. */
const Tooltip = TooltipPrimitive.Root;

/** Tooltip trigger component. */
const TooltipTrigger = TooltipPrimitive.Trigger;

/** Tooltip content container component. */
const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-[200px] overflow-hidden rounded-md border border-border bg-muted px-3 py-1.5 text-xs text-foreground shadow-md animate-in fade-in-0 zoom-in-95 duration-150 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
